import { Megaphone } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import MonthCard from "./MonthCard";

import EmptyState from "@/components/ui/v2/empty-state";
import { useInvoiceCampaignJobBoardMonthSpentMutation } from "@/features/campaigns/jobboards/api/useInvoiceCampaignJobBoardMonthSpentMutation";
import { useUpdateCampaignJobBoardMonthBudgetMutation } from "@/features/campaigns/jobboards/api/useUpdateCampaignJobBoardMonthBudgetMutation";
import { useUpdateCampaignJobBoardMonthSpentMutation } from "@/features/campaigns/jobboards/api/useUpdateCampaignJobBoardMonthSpentMutation";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";
import useToast from "@/lib/hooks/useToast";
import { JobboardCampaignType } from "@/types/response/CampaignJobBoardMonthType";

interface CampaignJobBoardMonthListProps {
  months: JobboardCampaignType[] | undefined;
  campaignId: string;
  clientIntegrationModuleId: string;
  clientId: string;
  minimumBudget?: number;
  vacancyCount?: number;
  startDate?: string;
}

interface MonthData {
  year: number;
  month: number;
  monthString: string;
  existingData: JobboardCampaignType;
}

function CampaignJobBoardMonthList({
  months,
  campaignId,
  clientIntegrationModuleId,
  clientId,
  minimumBudget,
  vacancyCount,
  startDate,
}: CampaignJobBoardMonthListProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { canEdit, canCreate } = usePermissionMatrix();
  const canEditSpent = canEdit("campaigns");
  const canCreateSpent = canCreate("campaigns");
  // Only admins and super admins can view spent (check EDIT permission which requires ADMIN+)
  const canViewSpent = canEdit("spent");

  const [editingBudgetMonth, setEditingBudgetMonth] = useState<string | null>(
    null
  );
  const [editingSpentMonth, setEditingSpentMonth] = useState<string | null>(
    null
  );
  const [editBudgetValue, setEditBudgetValue] = useState<string>("");
  const [editSpentValue, setEditSpentValue] = useState<string>("");
  const updateMonthBudget = useUpdateCampaignJobBoardMonthBudgetMutation();
  const invoiceMonthSpent = useInvoiceCampaignJobBoardMonthSpentMutation();
  const updateMonthSpent = useUpdateCampaignJobBoardMonthSpentMutation();

  const monthlyRecords = useMemo<MonthData[]>(() => {
    if (!months || months.length === 0) {
      return [];
    }

    // Backend sorts by year descending (newest first), then month descending
    return months.map((month) => {
      const year = Number.parseInt(month.month.substring(0, 4), 10);
      const monthNum = Number.parseInt(month.month.substring(5, 7), 10);
      const monthString = month.month.substring(0, 7);
      return {
        year,
        month: monthNum,
        monthString,
        existingData: month,
      };
    });
  }, [months]);

  // Group months by year (backend sorts years descending, so grouping preserves that order)
  const monthsByYear = useMemo(() => {
    const grouped = new Map<number, MonthData[]>();
    // Iterate through monthlyRecords in order (backend already sorted: current year, next year, previous years)
    monthlyRecords.forEach((monthData) => {
      if (!grouped.has(monthData.year)) {
        grouped.set(monthData.year, []);
      }
      grouped.get(monthData.year)!.push(monthData);
    });

    // Years are already in correct order from backend sorting, just convert map to array
    return Array.from(grouped.entries()).map(([year, months]) => ({
      year,
      months,
    }));
  }, [monthlyRecords]);

  const handleEditBudget = (monthData: MonthData) => {
    if (monthData.existingData.isInvoiced) {
      showToast(
        t("validation.budgetLocked", {
          defaultValue: "Budget cannot be edited once invoiced",
        }),
        "error"
      );
      return;
    }

    if (editingSpentMonth === monthData.monthString) {
      handleCancelSpent();
    }
    setEditingBudgetMonth(monthData.monthString);
    const editableValue = monthData.existingData.monthlyBudget ?? monthData.existingData.budget;
    setEditBudgetValue(Math.round(editableValue).toString());
  };

  const handleCancelBudget = () => {
    setEditingBudgetMonth(null);
    setEditBudgetValue("");
  };

  const handleSaveBudget = async (monthData: MonthData) => {
    if (monthData.existingData.isInvoiced) {
      showToast(
        t("validation.budgetLocked", {
          defaultValue: "Budget cannot be edited once invoiced",
        }),
        "error"
      );
      return;
    }

    const numericValue = Number.parseFloat(editBudgetValue);
    if (Number.isNaN(numericValue) || numericValue < 0) {
      showToast(
        t("validation.invalidBudget", {
          defaultValue: "Invalid budget amount",
        }),
        "error"
      );
      return;
    }

    // Ensure value is an integer (round to nearest integer)
    const integerValue = Math.round(numericValue);

    try {
      await updateMonthBudget.mutateAsync({
        campaignId,
        clientIntegrationModuleId,
        clientId,
        year: String(monthData.year),
        month: String(monthData.month).padStart(2, "0"),
        amount: integerValue,
      });
      setEditingBudgetMonth(null);
      setEditBudgetValue("");
      showToast(t("success.updated", { entity: t("common.budget") }));
    } catch {
      showToast(t("errors.somethingWentWrong"), "error");
    }
  };

  const handleEditSpent = (monthData: MonthData) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    const isFutureMonth =
      monthData.year > currentYear ||
      (monthData.year === currentYear && monthData.month > currentMonth);

    if (isFutureMonth) {
      showToast(
        t("validation.cannotAddSpentForFutureMonth", {
          defaultValue: "Cannot add spent for future months",
        }),
        "error"
      );
      return;
    }

    const hasSpent = monthData.existingData.actualSpent != null;
    const canEdit = hasSpent ? canEditSpent : canCreateSpent;

    if (!canEdit) {
      showToast(
        t("validation.noPermission", {
          defaultValue: "You do not have permission to edit spent",
        }),
        "error"
      );
      return;
    }

    if (editingBudgetMonth === monthData.monthString) {
      handleCancelBudget();
    }
    setEditingSpentMonth(monthData.monthString);
    setEditSpentValue(monthData.existingData.actualSpent?.toString() ?? "");
  };

  const handleCancelSpent = () => {
    setEditingSpentMonth(null);
    setEditSpentValue("");
  };

  const handleSaveSpent = async (monthData: MonthData) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    const isFutureMonth =
      monthData.year > currentYear ||
      (monthData.year === currentYear && monthData.month > currentMonth);

    if (isFutureMonth) {
      showToast(
        t("validation.cannotAddSpentForFutureMonth", {
          defaultValue: "Cannot add spent for future months",
        }),
        "error"
      );
      return;
    }

    const numericValue = Number.parseFloat(editSpentValue);
    if (
      editSpentValue.trim() !== "" &&
      (Number.isNaN(numericValue) || numericValue < 0)
    ) {
      showToast(
        t("validation.invalidAmount", { defaultValue: "Invalid spent amount" }),
        "error"
      );
      return;
    }

    const hasSpent = monthData.existingData.actualSpent != null;
    const isNew = !hasSpent;
    const mutation = isNew ? invoiceMonthSpent : updateMonthSpent;

    try {
      await mutation.mutateAsync({
        campaignId,
        clientIntegrationModuleId,
        clientId,
        year: String(monthData.year),
        month: String(monthData.month).padStart(2, "0"),
        amount: editSpentValue.trim() === "" ? 0 : numericValue,
      });
      setEditingSpentMonth(null);
      setEditSpentValue("");
      showToast(
        isNew
          ? t("success.created", { entity: t("common.spent") })
          : t("success.updated", { entity: t("common.spent") })
      );
    } catch {
      showToast(t("errors.somethingWentWrong"), "error");
    }
  };

  const getNumberValue = (value: string): string => {
    if (value.trim() === "") return "";
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? "" : value;
  };

  if (monthlyRecords.length === 0) {
    return (
      <EmptyState
        icon={Megaphone}
        title={t("common.noRecordsFound", {
          defaultValue: "No monthly records found",
        })}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {monthsByYear.map((yearGroup) => {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;

        return (
          <div key={yearGroup.year} className="flex flex-col gap-4">
            {/* Year Header */}
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">
                {yearGroup.year}
              </h3>
              <span className="text-sm text-muted-foreground">
                {yearGroup.months.length}{" "}
                {yearGroup.months.length === 1
                  ? t("common.month", { defaultValue: "month" })
                  : t("common.months", { defaultValue: "months" })}
              </span>
            </div>

            {/* Months Grid */}
            <div className="grid gap-5 grid-cols-2 md:grid-cols-3 3xl:grid-cols-4">
              {yearGroup.months.map((monthData) => {
                const isLoadingBudget =
                  updateMonthBudget.isPending &&
                  editingBudgetMonth === monthData.monthString;
                const isLoadingSpent =
                  (invoiceMonthSpent.isPending || updateMonthSpent.isPending) &&
                  editingSpentMonth === monthData.monthString;
                const isFutureMonth =
                  monthData.year > currentYear ||
                  (monthData.year === currentYear &&
                    monthData.month > currentMonth);
                const isPastMonth =
                  monthData.year < currentYear ||
                  (monthData.year === currentYear &&
                    monthData.month < currentMonth);

                return (
                  <MonthCard
                    key={`${campaignId}-${clientIntegrationModuleId}-${monthData.monthString}`}
                    monthString={monthData.monthString}
                    year={monthData.year}
                    month={monthData.month}
                    existingData={monthData.existingData}
                    editingBudgetMonth={editingBudgetMonth}
                    editingSpentMonth={editingSpentMonth}
                    editBudgetValue={editBudgetValue}
                    editSpentValue={editSpentValue}
                    isLoadingBudget={isLoadingBudget}
                    isLoadingSpent={isLoadingSpent}
                    canEditSpent={canEditSpent}
                    canCreateSpent={canCreateSpent}
                    canViewSpent={canViewSpent}
                    isFutureMonth={isFutureMonth}
                    isPastMonth={isPastMonth}
                    isCurrentMonth={monthData.year === currentYear && monthData.month === currentMonth}
                    minimumBudget={minimumBudget}
                    vacancyCount={vacancyCount}
                    onEditBudget={() => handleEditBudget(monthData)}
                    onSaveBudget={() => handleSaveBudget(monthData)}
                    onCancelBudget={handleCancelBudget}
                    onEditSpent={() => handleEditSpent(monthData)}
                    onSaveSpent={() => handleSaveSpent(monthData)}
                    onCancelSpent={handleCancelSpent}
                    onBudgetValueChange={setEditBudgetValue}
                    onSpentValueChange={setEditSpentValue}
                    getNumberValue={getNumberValue}
                    startDate={startDate}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default CampaignJobBoardMonthList;
